/**
 * MapaRutas.jsx
 * 
 * Componente que muestra un mapa interactivo con rutas y marcadores
 * 
 * Qué hace:
 * - Muestra un mapa usando Leaflet
 * - Dibuja la ruta optimizada como una línea
 * - Muestra marcadores numerados para cada punto de entrega
 * - Muestra la posición actual del repartidor
 * - Actualiza la posición del repartidor cuando se mueve
 * 
 * Qué recibe (props):
 * - ruta (Array): Array de coordenadas [lat, lng] para dibujar la ruta
 * - puntosEntrega (Array): Array de objetos {lat, lng, numero} con los puntos de entrega
 * - puntoInicial (Object): {lat, lng} del punto de partida
 * - posicionRepartidor (Object): {lat, lng} de la posición actual del repartidor (null si no hay)
 * - puntoActual (number): Índice del punto actual en la ruta (para resaltarlo)
 * 
 * Qué devuelve:
 * - JSX con el componente del mapa
 * 
 * Dónde modificarlo:
 * - Para cambiar el estilo de los marcadores
 * - Para cambiar el color de la ruta
 * - Para agregar más información en los popups
 */

import React, { useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'

// Importar iconos de Leaflet (necesario para que funcionen los marcadores)
import 'leaflet/dist/leaflet.css'

// Fix para los iconos de Leaflet en React
// Leaflet espera que los iconos estén en una ruta específica, esto los corrige
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

/**
 * Componente interno que ajusta la vista del mapa cuando cambian las coordenadas
 */
function AjustarVista({ puntos }) {
  const map = useMap()

  useEffect(() => {
    if (puntos && puntos.length > 0) {
      // Calcular los límites que incluyen todos los puntos
      const bounds = L.latLngBounds(puntos)
      // Ajustar la vista del mapa para mostrar todos los puntos
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [puntos, map])

  return null
}

/**
 * Componente que hace que el mapa siga al repartidor de forma suave (como Google Maps)
 * - Sigue al repartidor con panTo (movimiento suave)
 * - Ajusta el zoom solo cuando está cerca de una entrega
 * - Permite que el usuario mueva el mapa manualmente
 */
function SeguirRepartidor({ posicionRepartidor, puntosEntrega, puntoActual, activo }) {
  const map = useMap()
  const ultimoZoomRef = useRef(16) // Zoom por defecto
  const usuarioMovioMapaRef = useRef(false)
  const timeoutMovimientoRef = useRef(null)
  const ultimaPosicionRef = useRef(null)

  // Detectar cuando el usuario mueve el mapa manualmente
  useEffect(() => {
    if (!activo) return

    const handleDragStart = () => {
      usuarioMovioMapaRef.current = true
      // Después de 2 segundos, volver a seguir automáticamente
      if (timeoutMovimientoRef.current) {
        clearTimeout(timeoutMovimientoRef.current)
      }
      timeoutMovimientoRef.current = setTimeout(() => {
        usuarioMovioMapaRef.current = false
      }, 2000)
    }

    map.on('dragstart', handleDragStart)

    return () => {
      map.off('dragstart', handleDragStart)
      if (timeoutMovimientoRef.current) {
        clearTimeout(timeoutMovimientoRef.current)
      }
    }
  }, [map, activo])

  useEffect(() => {
    if (!activo || !posicionRepartidor) return

    // Si el usuario movió el mapa manualmente, no forzar el seguimiento
    if (usuarioMovioMapaRef.current) {
      return
    }

    // Calcular distancia al punto más cercano para ajustar zoom (solo para zoom, no para centrar)
    let distanciaMinima = Infinity

    if (puntosEntrega && puntosEntrega.length > 0) {
      puntosEntrega.forEach(punto => {
        const distancia = map.distance(
          [posicionRepartidor.lat, posicionRepartidor.lng],
          [punto.lat, punto.lng]
        )
        if (distancia < distanciaMinima) {
          distanciaMinima = distancia
        }
      })
    }

    // Determinar el zoom apropiado basado en la distancia
    let zoomObjetivo = ultimoZoomRef.current

    if (distanciaMinima < 100) {
      // Zoom cercano cuando está cerca de una entrega (zoom 18)
      zoomObjetivo = 18
    } else if (distanciaMinima < 300) {
      // Zoom intermedio cuando se está acercando (zoom 17)
      zoomObjetivo = 17
    } else {
      // Zoom normal cuando está en ruta (zoom 16)
      zoomObjetivo = 16
    }

    // Calcular si el repartidor se movió significativamente
    let debeMover = true
    if (ultimaPosicionRef.current) {
      const distanciaMovimiento = map.distance(
        [ultimaPosicionRef.current.lat, ultimaPosicionRef.current.lng],
        [posicionRepartidor.lat, posicionRepartidor.lng]
      )
      // Solo mover si se desplazó más de 3 metros (respuesta más rápida)
      debeMover = distanciaMovimiento > 3
    }

    if (debeMover) {
      // IMPORTANTE: Centrar el mapa en el repartidor (como Google Maps)
      // El repartidor SIEMPRE está en el centro, no la ruta ni los puntos de entrega
      const centroActual = map.getCenter()
      const distanciaCentro = map.distance(
        [centroActual.lat, centroActual.lng],
        [posicionRepartidor.lat, posicionRepartidor.lng]
      )

      // IMPORTANTE: Centrar el mapa en el repartidor (como Google Maps)
      // El repartidor SIEMPRE está en el centro del mapa
      const zoomActual = map.getZoom()
      
      // Actualizar zoom solo si cambió significativamente
      if (Math.abs(zoomActual - zoomObjetivo) > 0.5) {
        // Cambiar zoom y centrar en el repartidor
        map.setView(
          [posicionRepartidor.lat, posicionRepartidor.lng],
          zoomObjetivo,
          { animate: true, duration: 0.25 }
        )
        ultimoZoomRef.current = zoomObjetivo
      } else {
        // Solo centrar en el repartidor, mantener zoom actual
        map.setView(
          [posicionRepartidor.lat, posicionRepartidor.lng],
          zoomActual,
          { animate: true, duration: 0.25 }
        )
      }
      ultimaPosicionRef.current = { ...posicionRepartidor }
    }

  }, [posicionRepartidor, puntosEntrega, puntoActual, activo, map])

  return null
}

/**
 * Componente principal del mapa
 */
function MapaRutas({ 
  ruta = [], 
  puntosEntrega = [], 
  puntoInicial = null,
  posicionRepartidor = null,
  puntoActual = -1,
  onPuntoInicialCambiado = null, // Callback cuando se arrastra el punto inicial
  onPuntoEntregaCambiado = null,  // Callback cuando se arrastra un punto de entrega
  seguirRepartidor = false        // Si es true, el mapa sigue al repartidor
}) {
  // Referencia al mapa para poder controlarlo desde fuera si es necesario
  const mapRef = useRef(null)

  // Preparar todos los puntos para ajustar la vista
  const todosLosPuntos = []
  if (puntoInicial) {
    todosLosPuntos.push([puntoInicial.lat, puntoInicial.lng])
  }
  puntosEntrega.forEach(punto => {
    todosLosPuntos.push([punto.lat, punto.lng])
  })
  if (posicionRepartidor) {
    todosLosPuntos.push([posicionRepartidor.lat, posicionRepartidor.lng])
  }

  // Coordenadas por defecto (Buenos Aires, Argentina) si no hay puntos
  const centroPorDefecto = [-34.6037, -58.3816]
  const zoomPorDefecto = 13

  // Color de la ruta (azul)
  const colorRuta = '#2563eb'
  const colorRutaCompletada = '#10b981'

  // Crear icono personalizado para el punto inicial
  const iconoInicial = L.divIcon({
    className: 'marcador-inicial',
    html: '<div style="background-color: #10b981; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  })

  // Crear icono personalizado para el repartidor
  const iconoRepartidor = L.divIcon({
    className: 'marcador-repartidor',
    html: '<div style="background-color: #ef4444; width: 25px; height: 25px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); animation: pulse 2s infinite;"></div>',
    iconSize: [25, 25],
    iconAnchor: [12.5, 12.5]
  })

  // Crear iconos numerados para los puntos de entrega
  const crearIconoNumerado = (numero, esActual = false) => {
    const color = esActual ? '#f59e0b' : '#2563eb'
    return L.divIcon({
      className: 'marcador-numerado',
      html: `<div style="background-color: ${color}; width: 35px; height: 35px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 14px;">${numero}</div>`,
      iconSize: [35, 35],
      iconAnchor: [17.5, 17.5]
    })
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={puntoInicial ? [puntoInicial.lat, puntoInicial.lng] : centroPorDefecto}
        zoom={zoomPorDefecto}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        {/* Capa de tiles (mapa base) - Usando OpenStreetMap */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Ajustar la vista para mostrar todos los puntos (solo si no está siguiendo al repartidor) */}
        {!seguirRepartidor && <AjustarVista puntos={todosLosPuntos} />}
        
        {/* Seguir al repartidor durante la simulación */}
        {seguirRepartidor && posicionRepartidor && (
          <SeguirRepartidor 
            posicionRepartidor={posicionRepartidor}
            puntosEntrega={puntosEntrega}
            puntoActual={puntoActual}
            activo={seguirRepartidor}
          />
        )}

        {/* Dibujar la ruta como una línea */}
        {ruta && ruta.length > 1 && (
          <Polyline
            positions={ruta}
            pathOptions={{
              color: colorRuta,
              weight: 5,
              opacity: 0.7
            }}
          />
        )}

        {/* Marcador del punto inicial (arrastrable si hay callback) */}
        {puntoInicial && (
          <Marker 
            position={[puntoInicial.lat, puntoInicial.lng]} 
            icon={iconoInicial}
            draggable={!!onPuntoInicialCambiado}
            eventHandlers={{
              dragend: (e) => {
                if (onPuntoInicialCambiado) {
                  const nuevaPos = e.target.getLatLng()
                  onPuntoInicialCambiado({
                    lat: nuevaPos.lat,
                    lng: nuevaPos.lng
                  })
                }
              }
            }}
          >
            <Popup>
              <strong>Punto de Partida</strong>
              {onPuntoInicialCambiado && <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Arrastra para ajustar posición</p>}
            </Popup>
          </Marker>
        )}

        {/* Marcadores de los puntos de entrega (arrastrables si hay callback) */}
        {puntosEntrega.map((punto, index) => {
          const esActual = puntoActual === index
          return (
            <Marker
              key={index}
              position={[punto.lat, punto.lng]}
              icon={crearIconoNumerado(punto.numero || index + 1, esActual)}
              draggable={!!onPuntoEntregaCambiado}
              eventHandlers={{
                dragend: (e) => {
                  if (onPuntoEntregaCambiado) {
                    const nuevaPos = e.target.getLatLng()
                    onPuntoEntregaCambiado(index, {
                      lat: nuevaPos.lat,
                      lng: nuevaPos.lng
                    })
                  }
                }
              }}
            >
              <Popup>
                <div>
                  <strong>Entrega #{punto.numero || index + 1}</strong>
                  {punto.direccion && <p>{punto.direccion}</p>}
                  {esActual && <p style={{ color: '#f59e0b', fontWeight: 'bold' }}>📍 Próxima entrega</p>}
                  {onPuntoEntregaCambiado && <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#2563eb' }}>Arrastra para ajustar posición</p>}
                </div>
              </Popup>
            </Marker>
          )
        })}

        {/* Marcador de la posición actual del repartidor */}
        {posicionRepartidor && (
          <Marker position={[posicionRepartidor.lat, posicionRepartidor.lng]} icon={iconoRepartidor}>
            <Popup>
              <strong>Posición Actual del Repartidor</strong>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Estilos CSS para la animación del pulso del repartidor */}
      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
      `}</style>
    </div>
  )
}

export default MapaRutas

